// GET /me: which Xpedition2 person the Keycloak sign-in is linked to. Shared by the avatar and the
// LinkedAccountGate, so it is requested once.
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/api/client';
import { config } from '@/config';

export interface Me {
  personGuid: string;
  email?: string;
  /** An active paid subscription: Premium features are enabled (the API checks too). */
  premium?: boolean;
  /** The Premium features this account has; empty on the free plan. */
  features?: PremiumFeature[];
  /** The activity kept in the account (any app sets it); null until one is chosen. */
  activity?: 'hike' | 'ride' | 'drive' | 'greenlane' | null;
  /** What the free plan allows; null where there is no limit. */
  limits?: { libraryItems?: number | null; activitySummaries?: number | null };
}

/** The API's Premium feature names (PremiumFeatures.cs). `libraryCap` is the free plan's library limit. */
export type PremiumFeature =
  | 'premiumMaps'
  | 'mapOverlays'
  | 'mapPois'
  | 'aiSuggest'
  | 'activitySummary'
  | 'garmin'
  | 'strava'
  | 'multiDay'
  | 'routingProfiles'
  | 'bulkImport'
  | 'groupActivities'
  | 'libraryCap';

export const premiumFeatures: readonly PremiumFeature[] = [
  'premiumMaps',
  'mapOverlays',
  'mapPois',
  'aiSuggest',
  'activitySummary',
  'garmin',
  'strava',
  'multiDay',
  'routingProfiles',
  'bulkImport',
  'groupActivities',
  'libraryCap',
];

/** A failed GET /me. 404 with code 1040: the sign-in matches no Xpedition2 person; `details` says why. */
export class MeError extends Error {
  constructor(
    readonly status: number,
    readonly code?: string,
    readonly details: string[] = [],
  ) {
    super(`GET /me: ${status}${code ? ` (${code})` : ''}`);
  }

  get notLinked() {
    return this.status === 404;
  }
}

export function useMe() {
  return useQuery<Me, MeError>({
    queryKey: ['me'],
    enabled: !config.authDisabled,
    staleTime: Infinity,
    // "Not linked" won't change by asking again; other failures (e.g. a token not yet ready) may.
    retry: (count, error) => !error.notLinked && count < 2,
    // Nor when another component starts using it: refetching clears the error, so the LinkedAccountGate would
    // flip back to the app, whose components ask again - asking /me in a tight loop.
    retryOnMount: false,
    queryFn: async () => {
      const response = await apiFetch('/me');
      if (!response.ok) {
        const body = (await response.json().catch(() => ({}))) as { message?: string; details?: string[] };
        throw new MeError(response.status, body.message, body.details ?? []);
      }
      return (await response.json()) as Me;
    },
  });
}

/** Premium features are enabled: a paid subscriber (or, with sign-in turned off for development, everyone). */
export function usePremium(): boolean {
  const me = useMe();
  return config.authDisabled || me.data?.premium === true;
}

/** This account has a Premium feature. An API without the feature list yet: Premium has them all. */
export function useFeature(feature: PremiumFeature): boolean {
  const me = useMe();
  const premium = usePremium();
  const features = me.data?.features;
  return config.authDisabled || (features ? features.includes(feature) : premium);
}
